// EditorialPricing, the "Rate Card".
//
// A real magazine rate card: typographic, formal, almost legal. In the
// standard (post-beta) layout, two columns are laid out like tariff
// sheets in print with the Free and Pro plans. During the Founding
// Provider Beta, the same visual language collapses into a single
// centrepiece column so the message is unambiguous: 0% platform
// commission, no subscription, only the standard card processing fee.
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { Button } from '../ui/button';

const FREE_LINES = {
  headline: '15% commission, new clients',
  fine: ['0% commission on repeat clients', '1.75% + 20p payment processing'],
  inclusions: [
    'Unlimited service listings',
    'Calendar and booking management',
    'Messaging with booked clients',
    'Client reviews and ratings',
    'Basic analytics',
    'Monthly and quarterly reports',
    'Guest checkout support',
  ],
};

const PRO_LINES = {
  headline: '8% commission, new clients',
  fine: ['0% commission on repeat clients', '1.55% + 20p payment processing'],
  inclusions: [
    'Everything in Free',
    '8% commission, save 47%',
    'Lower processing fees',
    'Prospect messaging, reply to new enquiries',
    'Smart waitlists, auto-fill cancellations',
    'SMS booking alerts for you and clients',
    'HMRC-ready VAT and sales reports',
    'Advanced analytics dashboard',
    'Priority support',
  ],
};

const betaLines = (fee) => ({
  headline: `${fee}% provider fee during beta, Stripe costs included`,
  fine: [
    'No subscription fees',
    'Clients pay no booking fee',
    '0% fee on repeat clients and bookings through your own link (Stripe processing applies)',
    'Founding Provider early-adopter pricing when paid plans launch',
  ],
  inclusions: [
    'Receive online bookings 24/7',
    'Unlimited service listings',
    'Calendar and booking management',
    'Client messaging and reviews',
    'Smart waitlists',
    'Advanced business insights',
    'Help shape the future of Zuriya by suggesting and voting on new features',
    'Founding Provider badge on your public profile',
  ],
});

function RateCardColumn({ plan, tier, price, kicker, lines, ctaLabel, ctaTo, dark, recommended, testId }) {
  return (
    <article
      className={`relative flex flex-col h-full ${dark ? 'bg-[#1A1A1A] text-white' : 'bg-[#FAFAFA] text-[#1A1A1A]'} ${dark ? 'border-t-4 border-[#D4AF37]' : 'border border-stone-300'}`}
      data-testid={testId || (dark ? 'pricing-pro' : 'pricing-free')}
    >
      {/* Tariff sheet head: mono kicker + serif tier name + big numerals */}
      <header className={`px-8 lg:px-10 pt-8 lg:pt-10 pb-6 ${dark ? 'border-b border-white/15' : 'border-b border-stone-200'}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${dark ? 'text-[#D4AF37]' : 'text-stone-500'}`}>
              {plan}{recommended ? ' \u00B7 Recommended' : ''}
            </p>
            <h3 className="font-heading text-3xl lg:text-4xl font-medium mt-2 leading-none">
              {tier}
            </h3>
          </div>
          <div className="text-right">
            <p className="font-heading text-5xl lg:text-6xl leading-none tracking-tight">
              {price}
            </p>
            <p className={`font-mono text-[10px] tracking-[0.25em] uppercase mt-2 ${dark ? 'text-white/50' : 'text-stone-400'}`}>
              {kicker}
            </p>
          </div>
        </div>
      </header>

      {/* Rate body: headline rate line + fine print, all in mono so the
          tariff sheet feels official. */}
      <div className={`px-8 lg:px-10 py-6 ${dark ? 'border-b border-white/15' : 'border-b border-stone-200'}`}>
        <p className={`font-heading text-xl ${dark ? 'text-[#D4AF37]' : 'text-[#1A1A1A]'}`}>
          {lines.headline}
        </p>
        <ul className="mt-3 space-y-1 font-mono text-xs">
          {lines.fine.map((f) => (
            <li key={f} className={dark ? 'text-white/60' : 'text-stone-500'}>
              {f}
            </li>
          ))}
        </ul>
      </div>

      {/* Inclusions, serif body. No tick icons; the hairline rules and
          spacing carry the structure instead. */}
      <ul className={`px-8 lg:px-10 py-6 flex-1 divide-y ${dark ? 'divide-white/10' : 'divide-stone-200'}`}>
        {lines.inclusions.map((line) => (
          <li key={line} className={`py-2.5 text-sm ${dark ? 'text-white/85' : 'text-stone-700'}`}>
            {line}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <footer className="px-8 lg:px-10 pb-8 lg:pb-10 pt-2">
        <Link to={ctaTo}>
          <Button
            className={`w-full h-12 rounded-none font-medium tracking-wide ${
              dark
                ? 'bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#e8c547]'
                : 'bg-[#1A1A1A] text-white hover:bg-[#2a2a2a]'
            }`}
            data-testid={testId ? `${testId}-cta` : (dark ? 'pricing-pro-cta' : 'pricing-free-cta')}
          >
            {ctaLabel}
          </Button>
        </Link>
      </footer>
    </article>
  );
}

export function EditorialPricing() {
  const { betaConfig } = useAuth();
  const betaMode = !!betaConfig?.beta_mode;
  const feePct = betaConfig?.provider_fee_percent;

  return (
    <section
      id="pricing"
      className="bg-[#F9F6F0] paper-grain py-20 lg:py-28 border-t border-stone-200"
      data-testid="pricing-section"
      data-beta-mode={betaMode ? 'true' : 'false'}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Masthead-style header */}
        <div className="border-b border-stone-300 pb-8 mb-10 lg:mb-14">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-500">
                The Rate Card
              </p>
              {betaMode ? (
                <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#1A1A1A] mt-3 leading-[1.05]">
                  One plan, for now. <span className="italic text-stone-500">Founding rates.</span>
                </h2>
              ) : (
                <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#1A1A1A] mt-3 leading-[1.05]">
                  Two plans. <span className="italic text-stone-500">No surprises.</span>
                </h2>
              )}
            </div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-500">
              {betaMode ? 'Beta \u00B7 Spring 2026' : 'Effective Spring 2026'}
            </p>
          </div>
          {betaMode && (
            <p className="mt-6 max-w-2xl text-sm text-stone-600 leading-relaxed">
              We&apos;re currently welcoming our first cohort of Founding Providers across the UK.
              During beta, there are no subscriptions and clients pay no booking fee. A single{' '}
              {feePct}% fee comes out of payouts on new marketplace clients, with Stripe costs included,
              and repeat clients or bookings through your own link carry no platform fee (Stripe processing still applies).
            </p>
          )}
          {betaMode && (
            <p
              className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]"
              data-testid="pricing-beta-urgency"
            >
              Applications open for our first Founding Provider cohort
            </p>
          )}
        </div>

        {betaMode ? (
          <div className="max-w-xl mx-auto">
            <RateCardColumn
              plan="Founding Provider"
              tier="Beta"
              price="&pound;0"
              kicker="Per month"
              lines={betaLines(feePct)}
              ctaLabel="Apply as a Founding Provider"
              ctaTo="/register?role=provider"
              dark
              recommended
              testId="pricing-beta"
            />
            <p
              className="mt-8 max-w-xl mx-auto text-center text-sm text-stone-600 leading-relaxed"
              data-testid="pricing-beta-reassurance"
            >
              When paid plans are introduced in the future, Founding Providers will receive
              plenty of notice along with exclusive early-adopter pricing.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 lg:gap-6 items-stretch">
            <RateCardColumn
              plan="Plan A"
              tier="Free"
              price="&pound;0"
              kicker="Per month"
              lines={FREE_LINES}
              ctaLabel="Start free"
              ctaTo="/register?role=provider"
            />
            <RateCardColumn
              plan="Plan B"
              tier="Pro"
              price="&pound;25"
              kicker="Per month"
              lines={PRO_LINES}
              ctaLabel="Start with Pro"
              ctaTo="/register?role=provider"
              dark
              recommended
            />
          </div>
        )}

        {/* Colophon-style fine print */}
        <p
          className="text-center font-mono text-[10px] tracking-[0.25em] uppercase text-stone-500 mt-10"
          data-testid="pricing-fine-print"
        >
          {betaMode
            ? `${feePct}% provider fee on new clients, Stripe included \u00B7 No booking fee for clients \u00B7 No surprises at payout`
            : 'Flat processing rates across all card types \u00B7 1.75% + 20p Free \u00B7 1.55% + 20p Pro \u00B7 No surprises at payout'}
        </p>
      </div>
    </section>
  );
}
